import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LogIn, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { TwoFactorLogin } from '@/components/TwoFactorLogin';

const NBR_LOGO = "https://nationalbusinessregister.co.uk/wp-content/uploads/2024/11/logo.png";

export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');
  
  const { login, setUser, setToken } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        username: email,
        password: password
      });
      
      // Check if 2FA is required (user has 2FA enabled)
      if (response.data.requires_2fa) {
        setRequires2FA(true);
        setTempToken(response.data.temp_token);
        setLoading(false);
        return;
      }
      
      // Normal login - check if 2FA needs to be set up
      const { access_token, user, needs_2fa_setup } = response.data;
      localStorage.setItem('auth_token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setToken(access_token);
      setUser(user);
      
      // Redirect to mandatory 2FA setup if not enabled
      if (needs_2fa_setup) {
        toast.info('Please set up Two-Factor Authentication to continue');
        navigate('/setup-2fa');
        return;
      }
      
      toast.success('Login successful');
      navigate('/dashboard');
    } catch (err) {
      const message = err.response?.data?.detail || 'Login failed. Please check your credentials.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handle2FASuccess = (data) => {
    const { access_token, user } = data;
    localStorage.setItem('auth_token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(user);
    
    toast.success('Login successful');
    navigate('/dashboard');
  };

  const handleBack2FA = () => {
    setRequires2FA(false);
    setTempToken('');
    setPassword('');
  };

  // Show 2FA verification screen
  if (requires2FA) {
    return (
      <TwoFactorLogin 
        tempToken={tempToken}
        onSuccess={handle2FASuccess}
        onBack={handleBack2FA}
      />
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="noise-overlay" />
      
      <div className="w-full max-w-md">
        {/* Coming Soon Banner */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-center">
          <p className="text-amber-800 font-semibold">Coming Soon</p>
          <p className="text-amber-600 text-sm mt-1">
            The NBR Trade Mark Monitoring dashboard is launching soon. 
            We'll email you when your account is ready.
          </p>
          <a href="/" className="text-teal-600 hover:text-teal-700 text-sm font-medium mt-2 inline-block">
            ← Return to Home
          </a>
        </div>

        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <img 
            src={NBR_LOGO} 
            alt="National Business Register" 
            className="h-12 w-auto"
          />
          <div>
            <h1 className="font-heading font-black text-2xl tracking-tight text-slate-900">MARK</h1>
            <p className="text-xs text-teal-600 tracking-widest uppercase">Trade Mark Monitoring</p>
          </div>
        </div>

        {/* Login Card */}
        <div className="swiss-card p-8" data-testid="login-card">
          <h2 className="font-heading font-bold text-xl text-slate-900 mb-2">Welcome back</h2>
          <p className="text-slate-500 text-sm mb-6">Sign in to your account to continue</p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-center gap-2" data-testid="login-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                data-testid="login-email-input"
                className="h-11"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link 
                  to="/forgot-password" 
                  className="text-xs text-slate-500 hover:text-slate-900"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                data-testid="login-password-input"
                className="h-11"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-11 bg-slate-900 hover:bg-slate-800"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  <LogIn className="w-4 h-4 mr-2" />
                  Sign In
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-slate-500">
              Don't have an account?{' '}
              <Link 
                to="/register" 
                className="text-slate-900 font-medium hover:underline"
                data-testid="register-link"
              >
                Request access
              </Link>
            </p>
          </div>
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">
          UK Trademark Monitoring System
        </p>
      </div>
    </div>
  );
};
